import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import * as _ from 'lodash';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthenticationService, ClientService } from '@services/services.module';
import { SurvaleCommonComponent } from '../../../../common/survale-common.component';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-client-email-config',
    templateUrl: './client-email-config.component.html',
    styleUrls: ['./client-email-config.component.scss'],
    standalone: false
})
export class ClientEmailConfigComponent extends SurvaleCommonComponent implements OnInit, OnChanges, OnDestroy {
  @Input() settings: any;

  public emailConfigForm: UntypedFormGroup;
  public hideSmtpPassword = true;
  public hideInternalSmtpPassword = true;
  public testingMainProvider = false;
  public testingInternalProvider = false;
  public tlsMinVersionOptions = [
    { label: 'TLS 1.3 (Recommended)', value: 'TLSv1.3' },
    { label: 'TLS 1.2', value: 'TLSv1.2' },
    { label: 'TLS 1.1 (Legacy/Insecure)', value: 'TLSv1.1' },
    { label: 'TLS 1.0 (Legacy/Insecure)', value: 'TLSv1' },
  ];
  public defaultEmailFooterText = 'This email is being sent from an unmonitored email address.' +
    'We can be reached at contactus@survale.com.<br/>\n' +
    'Survale offices are located at <br/>\n' +
    '2021 Fillmore St. #9035, San Francisco, CA, 94115<br/>\n' +
    '(888) 20-SURVALE<br/>\n' +
    '<a href="https://survale.com">https://survale.com</a>';
  // Guards the TLS Server Name auto-fill listeners against firing while ngOnChanges is programmatically patching the form
  private isPatchingForm = false;
  private readonly _onDestroy = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    public authenticationService: AuthenticationService,
    private toastrService: ToastrService,
    private clientService: ClientService,
  ) {
    super(authenticationService);
  }

  ngOnInit(): void {
    this.emailConfigForm = this.fb.group({
      isMailBoxMonitored: new UntypedFormControl(false),
      identifyClientsInUnsubscribeLink: new UntypedFormControl(false),
      hideEmailTrackingCode: new UntypedFormControl(false),
      weeklyEmailEnabled: new UntypedFormControl(false),
      excludeUnsubscribeLinkInEmail: new UntypedFormControl(false),
      emailProvider: new UntypedFormControl(),
      smtpHost: new UntypedFormControl(),
      smtpPort: new UntypedFormControl(),
      smtpSecure: new UntypedFormControl(true),
      smtpAuthUser: new UntypedFormControl(),
      smtpAuthPass: new UntypedFormControl(),
      smtpTlsRejectUnauthorized: new UntypedFormControl(),
      smtpTlsMinVersion: new UntypedFormControl(),
      smtpTlsServername: new UntypedFormControl(),
      smtpTlsCiphers: new UntypedFormControl(),
      replyToEmail: new UntypedFormControl(),
      replyToName: new UntypedFormControl(),
      systemReplyToEmail: new UntypedFormControl(),
      systemReplyToName: new UntypedFormControl(),
      header_from: new UntypedFormControl(),
      smtp_mailfrom: new UntypedFormControl(),
      unsubscribeUrl: new UntypedFormControl(),
      emailFooterText: new UntypedFormControl(),
      unsubscribePrefix: new UntypedFormControl(),
      internalEmailProviderEnabled: new UntypedFormControl(false),
      internalSmtpHost: new UntypedFormControl(),
      internalSmtpPort: new UntypedFormControl(),
      internalSmtpSecure: new UntypedFormControl(true),
      internalSmtpAuthUser: new UntypedFormControl(),
      internalSmtpAuthPass: new UntypedFormControl(),
      internalSmtpTlsRejectUnauthorized: new UntypedFormControl(),
      internalSmtpTlsMinVersion: new UntypedFormControl(),
      internalSmtpTlsServername: new UntypedFormControl(),
      internalSmtpTlsCiphers: new UntypedFormControl(),
      internalFromEmail: new UntypedFormControl(),
      internalFromName: new UntypedFormControl(),
      clientEmailDomain: new UntypedFormControl(),
    });

    this.autoFillTlsServername('smtpHost', 'smtpTlsServername');
    this.autoFillTlsServername('internalSmtpHost', 'internalSmtpTlsServername');
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  /**
   * TLS server name is almost always the same as the SMTP host, so as the user types the host,
   * mirror it into the TLS server name field live - but only while that field is still blank
   * (i.e. don't clobber a value the user has deliberately entered).
   */
  private autoFillTlsServername(hostControlName: string, servernameControlName: string): void {
    this.emailConfigForm.get(hostControlName).valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((host: string): void => {
        if (this.isPatchingForm) { return; }
        const servernameCtrl = this.emailConfigForm.get(servernameControlName);
        if (!servernameCtrl.value) {
          servernameCtrl.setValue(host, { emitEvent: false });
        }
      });
  }

  ngOnChanges(): void {
    if (!this.settings || !this.emailConfigForm) { return; }

    this.settings.emailFooterText = this.settings.emailFooterText || this.defaultEmailFooterText;
    this.settings.replyToEmail = this.settings.replyToEmail || 'no-reply@survale.com';
    _.defaults(this.settings, { excludeUnsubscribeLinkInEmail: false });
    const defaultsFalse = ['identifyClientsInUnsubscribeLink', 'isMailBoxMonitored', 'hideEmailTrackingCode', 'weeklyEmailEnabled'];
    _.each(defaultsFalse, v => { this.settings[v] = this.settings[v] || false; });

    for (const field of 'header_from,smtp_mailfrom,unsubscribeUrl'.split(',')) {
      this.settings[field] = _.get(this.settings, `sesExConfig.${field}`, '');
    }
    for (const field of 'host,port,secure'.split(',')) {
      const key = `smtp${field.charAt(0).toUpperCase() + field.slice(1)}`;
      this.settings[key] = _.get(this.settings, `smtpConfig.${field}`, field === 'secure' ? true : '');
    }
    for (const field of 'user,pass'.split(',')) {
      const key = `smtpAuth${field.charAt(0).toUpperCase() + field.slice(1)}`;
      this.settings[key] = _.get(this.settings, `smtpConfig.auth.${field}`, '');
    }
    for (const field of 'rejectUnauthorized,minVersion,servername,ciphers'.split(',')) {
      const key = `smtpTls${field.charAt(0).toUpperCase() + field.slice(1)}`;
      this.settings[key] = _.get(this.settings, `smtpConfig.tls.${field}`, '');
    }

    this.settings.internalEmailProviderEnabled = _.get(this.settings, 'internalEmailProvider.enabled', false);
    for (const field of 'host,port,secure'.split(',')) {
      const key = `internalSmtp${field.charAt(0).toUpperCase() + field.slice(1)}`;
      this.settings[key] = _.get(this.settings, `internalEmailProvider.${field}`, field === 'secure' ? true : '');
    }
    for (const field of 'user,pass'.split(',')) {
      const key = `internalSmtpAuth${field.charAt(0).toUpperCase() + field.slice(1)}`;
      this.settings[key] = _.get(this.settings, `internalEmailProvider.auth.${field}`, '');
    }
    for (const field of 'rejectUnauthorized,minVersion,servername,ciphers'.split(',')) {
      const key = `internalSmtpTls${field.charAt(0).toUpperCase() + field.slice(1)}`;
      this.settings[key] = _.get(this.settings, `internalEmailProvider.tls.${field}`, '');
    }
    this.settings.internalFromEmail = _.get(this.settings, 'internalEmailProvider.fromEmail', '');
    this.settings.internalFromName = _.get(this.settings, 'internalEmailProvider.fromName', '');

    this.isPatchingForm = true;
    this.emailConfigForm.patchValue(this.settings);
    this.isPatchingForm = false;
  }

  addToCorporateEmailDomain(): void {
    if (this.emailConfigForm.value?.clientEmailDomain && this.emailConfigForm.value?.clientEmailDomain?.trim().length > 0) {
      this.settings.corporateEmailDomains = this.settings.corporateEmailDomains || [];
      this.settings.corporateEmailDomains.push(this.emailConfigForm.value.clientEmailDomain.trim());
      this.emailConfigForm.get('clientEmailDomain').setValue('');
    } else {
      this.toastrService.error('Please enter a valid email domain');
    }
  }

  removeFromCorporateEmailDomain(domain: string): void {
    this.settings.corporateEmailDomains = this.settings.corporateEmailDomains.filter((d: string): boolean => d !== domain);
  }

  /**
   * Builds the smtpConfig object (used by the primary SMTP_MAILER provider) from the form's current
   * values - the same shape client.component.ts persists on save.
   */
  buildSmtpConfig(): any {
    const {
      smtpHost, smtpPort, smtpSecure, smtpAuthUser, smtpAuthPass,
      smtpTlsRejectUnauthorized, smtpTlsMinVersion, smtpTlsServername, smtpTlsCiphers
    } = this.emailConfigForm.value;
    return {
      host: smtpHost,
      port: smtpPort,
      secure: smtpSecure,
      auth: { user: smtpAuthUser, pass: smtpAuthPass },
      tls: {
        rejectUnauthorized: smtpTlsRejectUnauthorized,
        minVersion: smtpTlsMinVersion,
        servername: smtpTlsServername || smtpHost,
        ciphers: smtpTlsCiphers
      }
    };
  }

  /** Builds the sesExConfig object from the form's current values. */
  buildSesExConfig(): any {
    const { header_from, smtp_mailfrom, unsubscribeUrl } = this.emailConfigForm.value;
    return { header_from, smtp_mailfrom, unsubscribeUrl };
  }

  /**
   * Builds the internalEmailProvider object from the form's current values - the same shape
   * client.component.ts persists on save.
   */
  buildInternalProviderConfig(): any {
    const {
      internalEmailProviderEnabled,
      internalSmtpHost, internalSmtpPort, internalSmtpSecure,
      internalSmtpAuthUser, internalSmtpAuthPass,
      internalSmtpTlsRejectUnauthorized, internalSmtpTlsMinVersion, internalSmtpTlsServername, internalSmtpTlsCiphers,
      internalFromEmail, internalFromName
    } = this.emailConfigForm.value;
    return {
      enabled: internalEmailProviderEnabled,
      host: internalSmtpHost,
      port: internalSmtpPort,
      secure: internalSmtpSecure,
      auth: { user: internalSmtpAuthUser, pass: internalSmtpAuthPass },
      tls: {
        rejectUnauthorized: internalSmtpTlsRejectUnauthorized,
        minVersion: internalSmtpTlsMinVersion,
        servername: internalSmtpTlsServername || internalSmtpHost,
        ciphers: internalSmtpTlsCiphers
      },
      fromEmail: internalFromEmail,
      fromName: internalFromName
    };
  }

  async testMainEmailProvider(emailTo: string): Promise<void> {
    const { emailProvider, replyToEmail, replyToName, systemReplyToEmail, systemReplyToName } = this.emailConfigForm.value;
    const config = {
      emailProvider,
      smtpConfig: this.buildSmtpConfig(),
      sesExConfig: this.buildSesExConfig(),
      replyToEmail, replyToName, systemReplyToEmail, systemReplyToName
    };
    await this.sendTestEmail('main', emailTo, config, (loading): boolean => this.testingMainProvider = loading);
  }

  async testInternalEmailProvider(emailTo: string): Promise<void> {
    await this.sendTestEmail('internal', emailTo, this.buildInternalProviderConfig(),
      (loading): boolean => this.testingInternalProvider = loading);
  }

  private async sendTestEmail(
    provider: 'main' | 'internal', emailTo: string, config: any, setLoading: (loading: boolean) => void
  ): Promise<void> {
    if (!emailTo || !emailTo.trim()) {
      this.toastrService.error('Please enter an email address to send the test to');
      return;
    }
    setLoading(true);
    try {
      await firstValueFrom<any>(this.clientService.testEmailProvider(provider, emailTo.trim(), config));
      this.toastrService.success(`Test email sent via the ${provider === 'main' ? 'Main' : 'Internal'} Email Provider.`);
    } catch (error) {
      console.error('Error testing email provider:', error);
      this.toastrService.error(error?.error?.error || error?.message || 'Error sending test email.');
    } finally {
      setLoading(false);
    }
  }
}
